import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { toast } from "react-toastify";
import UserIcon from "@icons/user-01.svg?react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { BuildingOffice2Icon, ClockIcon, EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import UploadIcon from "@icons/upload-01.svg?react";
import SettingsIcon from "@icons/settings-02.svg?react";
import HelpCircleIcon from "@icons/help-circle.svg?react";
import LogOutIcon from "@icons/log-out-02.svg?react";
import CreditCardIcon from "@icons/credit-card-02.svg?react";
import Trash01Icon from "@icons/trash-01.svg?react";
import AnnouncementIcon from "@icons/announcement-03.svg?react";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "@/common/hooks/useRedux";
import { signOutLocal } from "@common/lib/signOut";
import { clearLocalStorageExceptAccountScopedPreferences } from "@common/lib/clearAccountScopedLocalStorage";
import { NAV_ICON_BUTTON_FILLED_CLASS, NAV_ICON_BUTTON_FILLED_HOVER_CLASS } from "./navIconButton";
import { clearDoctorState } from "@/state/redux/doctorSlice";
import { clearResourceState } from "@/state/redux/resourcesSlice";
import { clearDocumentState } from "@/state/redux/documentSlice";
import * as Sentry from "@sentry/react";
import LocalStorageModal from "@/common/components/local-storage-modal/LocalStorageModal";
import { RecordingState } from "@/common/utils/types";
import { useAccountContext } from "@/state/providers/AccountProvider";
import RouteIcon from "@icons/route.svg?react";
import CursorClickIcon from "@icons/cursor-click-02.svg?react";
import classNames from "classnames";
import { useDataContextApi } from "@/state/providers/DataProvider";
import { useImpersonation } from "@/state/providers/ImpersonationProvider";
import { useImpersonationAwareUser } from "@/common/hooks/useImpersonationAwareUser";
import { useBillingPlan } from "@/state/hooks/useBilling";
import ArrowSquareUpIcon from "@icons/arrow-square-up.svg?react";
import PlanUpgradeModal from "@/features/settings/components/billing/PlanUpgradeModal";
import { ENDPOINTS } from "@/common/utils/url-registry";
import { useMemberships } from "@/state/hooks/useMemberships";
import { createCoalescingIdleRunner, isRecorderActive, type CoalescingIdleRunner } from "@/common/lib/recorderActivity";
import {
    canDialFromThisDevice,
    SUPPORT_EMAIL,
    SUPPORT_HOURS_SHORT,
    SUPPORT_PHONE_DISPLAY,
    SUPPORT_PHONE_E164,
} from "@common/lib/supportHours";

/**
 * Profile menu component
 * @returns React.FC
 * @example <ProfileMenu />
 */
const ProfileMenu: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { accountData } = useAccountContext();
    const { incompleteUploads } = useDataContextApi();
    const { isAdmin } = useImpersonation();
    const { isImpersonating, impersonationSession, userId: effectiveUserId } = useImpersonationAwareUser();

    const { globalRecordingState, isUploadingGlobal, completing, finalizing } = useAppSelector(
        (state) => state.globalState,
    );
    const { data: billingPlan } = useBillingPlan({ enabled: !!accountData?.is_self_serve });
    const isTrial = billingPlan?.billing_type === "trial";

    const [openModal, setOpenModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    const { data: membershipsData } = useMemberships(effectiveUserId ?? undefined);

    const showSwitchClinic = (membershipsData?.account_ids?.length ?? 0) > 1;

    // EN-837: "Sign out" and "Switch clinic" defer their teardown until the recorder is idle when
    // clicked mid-recording (the dropdown can be open when an upload starts). Route each through a
    // coalescing runner so repeated clicks don't stack multiple deferred teardowns (each leaking a
    // store subscription), and cancel both on unmount so a stale nav/sign-out can't fire minutes
    // after the vet has moved on. Mirrors AccountProvider's deferred-sign-out handling.
    // Lazy-init so the factory runs once, not on every render (useRef evaluates its arg each render
    // and discards all but the first — a wasted allocation per render).
    const deferredSignOutRunner = useRef<CoalescingIdleRunner | null>(null);
    const deferredSwitchClinicRunner = useRef<CoalescingIdleRunner | null>(null);
    if (!deferredSignOutRunner.current) deferredSignOutRunner.current = createCoalescingIdleRunner();
    if (!deferredSwitchClinicRunner.current) deferredSwitchClinicRunner.current = createCoalescingIdleRunner();
    useEffect(() => {
        const signOutRunner = deferredSignOutRunner.current;
        const switchClinicRunner = deferredSwitchClinicRunner.current;
        return () => {
            signOutRunner?.cancel();
            switchClinicRunner?.cancel();
        };
    }, []);

    /**
     * Sign out the user
     * @param {Event} e - The event object
     * @returns {Promise<void>}
     * */
    const performSignOut = async (): Promise<void> => {
        try {
            // Route through signOutLocal so the "sign out this device only" guarantee, the try/catch,
            // and the Sentry errorCategory:"auth" tagging are shared with every other sign-out site
            // (it reports the error internally and never rejects). EN-837.
            const { error } = await signOutLocal();
            dispatch(clearDoctorState());
            dispatch(clearResourceState());
            dispatch(clearDocumentState());
            if (error) {
                // Clearing specific local storage key based on a pattern. In case auth breaks down.
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.match(/^sb-.*-auth-token$/)) {
                        localStorage.removeItem(key);
                    }
                }
                window.location.href = "/";
            } else {
                // Not a blanket wipe-everything call: that would also clear account-scoped
                // preferences (working hours, column widths, doctor filter, etc.) that are
                // deliberately keyed by accountId so they survive a logout on the same
                // clinic account/shared workstation.
                clearLocalStorageExceptAccountScopedPreferences();
                window.location.href = "/";
            }
        } catch (error) {
            Sentry.captureException(error);
        }
    };

    const signOut = async (e: { preventDefault: () => void }): Promise<void> => {
        e.preventDefault();
        // EN-837: the account menu is disabled while recording/uploading, but if the dropdown was
        // already open when an upload started the item can still be activated. Never tear down an
        // in-flight recording/upload — defer the whole sign-out (state clear + hard-nav) until idle.
        if (isRecorderActive()) {
            deferredSignOutRunner.current?.schedule(() => {
                void performSignOut();
            });
            return;
        }
        await performSignOut();
    };

    /**
     * Open a link in a new tab
     * @param {string} link - The link to open
     * @param {boolean} openInNewTab - Whether to open in a new tab
     * @returns {void}
     * */
    function openLink(link: string, openInNewTab: boolean = false) {
        if (openInNewTab) {
            window.open(link, "_blank", "noopener,noreferrer");
        } else {
            window.open(link, "_self");
        }
    }

    const isRecording =
        globalRecordingState === RecordingState.recording || globalRecordingState === RecordingState.paused;

    // EN-837: the teardown-sensitive controls (Sign Out, Switch clinic) defer on isRecorderActive(),
    // which also counts `completing` (doc completion). Gate their disabled state on the SAME predicate
    // so a click during completing-only mode can't silently schedule a deferred teardown with no
    // feedback — a greyed, disabled control is the feedback. Mirrors selectRecorderActive.
    const isRecorderBusy = isRecording || isUploadingGlobal || completing || finalizing;

    // Static per device; computed once rather than on every render.
    const canDial = useMemo(() => canDialFromThisDevice(), []);

    return (
        <Fragment>
            <Menu as="div" className="relative inline-block text-left">
                <div className="relative ">
                    <MenuButton
                        disabled={isRecorderBusy}
                        className={classNames(
                            NAV_ICON_BUTTON_FILLED_CLASS,
                            "relative",
                            isRecorderBusy ? "opacity-50 cursor-not-allowed" : NAV_ICON_BUTTON_FILLED_HOVER_CLASS,
                        )}
                        data-tour="settings-nav"
                        title="Account"
                        aria-label="Account menu"
                    >
                        <UserIcon className="w-5 h-5 text-color-600" />
                        {incompleteUploads.length > 0 && (
                            <div
                                style={{
                                    top: "-4px",
                                    right: "-4px",
                                }}
                                className="absolute h-2 w-2 bg-red-600 rounded-full"
                            />
                        )}
                    </MenuButton>
                </div>
                <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                >
                    <MenuItems
                        modal={false}
                        className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-200 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
                    >
                        {/* Clinic Info Section */}
                        <div className="px-1 py-1">
                            <div className="flex w-full items-center rounded-md px-2 py-2 text-sm text-gray-700">
                                <div className="flex items-center justify-center p-2 bg-max-25 rounded-lg border border-solid border-gray-200 max-h-[36px] max-w-[36px] mr-2">
                                    <UserIcon className="w-5 h-5 text-color-600 bg-max-25" />
                                </div>
                                <div className="flex flex-col min-w-0 flex-1">
                                    <span className="font-medium truncate">{accountData?.clinic_name || ""}</span>
                                    <span className="text-xs text-gray-500 truncate">
                                        {accountData?.auth_email || ""}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* EN-893: support contact details, directly under the account identity so
                            they're visible on open. Deliberately NOT MenuItems — none of it is
                            actionable except the phone on touch devices, so it stays out of the
                            arrow-key focus order. Text is selectable so it can be copied. */}
                        <div className="px-3 py-2 select-text">
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                                Contact Support
                            </div>
                            <div className="space-y-1.5 text-xs text-gray-600">
                                <div className="flex items-center gap-2">
                                    <EnvelopeIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                                    <span className="truncate">{SUPPORT_EMAIL}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <PhoneIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                                    {/* iOS Safari linkifies a bare number with no markup, so on touch devices
                                        we mark it up ourselves to control the styling. Suppressed while the
                                        recorder is busy: a tel: tap backgrounds the browser, which is how web
                                        recordings get stranded (EN-860). The menu button is disabled during
                                        recording, but the dropdown can already be open when one starts. */}
                                    {canDial && !isRecorderBusy ? (
                                        <a
                                            href={`tel:${SUPPORT_PHONE_E164}`}
                                            className="underline underline-offset-2"
                                            aria-label={`Call HappyDoc support at ${SUPPORT_PHONE_DISPLAY}. Open ${SUPPORT_HOURS_SHORT}`}
                                        >
                                            {SUPPORT_PHONE_DISPLAY}
                                        </a>
                                    ) : (
                                        <span>{SUPPORT_PHONE_DISPLAY}</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <ClockIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
                                    <span>{SUPPORT_HOURS_SHORT}</span>
                                </div>
                            </div>
                        </div>

                        {isTrial && (
                            <div className="px-2 py-1.5">
                                <MenuItem>
                                    {({ focus }) => (
                                        <button
                                            disabled={isRecording}
                                            onClick={() => setShowUpgradeModal(true)}
                                            className={`${
                                                focus ? "bg-max-800" : "bg-max-900"
                                            } group flex w-full items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white transition-colors`}
                                        >
                                            <ArrowSquareUpIcon className="mr-2 h-4 w-4 text-white" aria-hidden="true" />
                                            Upgrade Plan
                                        </button>
                                    )}
                                </MenuItem>
                            </div>
                        )}

                        <div className="px-1 py-1 ">
                            <MenuItem>
                                {({ focus }) => (
                                    <button
                                        disabled={isRecording}
                                        onClick={() => setOpenModal(true)}
                                        className={`${
                                            focus
                                                ? "bg-max-900 text-white"
                                                : incompleteUploads.length > 0
                                                  ? "text-red-500 "
                                                  : "text-gray-700"
                                        } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                        <UploadIcon
                                            className={`mr-2 h-4 w-4 ${focus ? "text-white" : incompleteUploads.length > 0 ? "text-red-500" : "text-gray-700"}`}
                                            aria-hidden="true"
                                        />
                                        Incomplete Uploads
                                    </button>
                                )}
                            </MenuItem>
                            <MenuItem>
                                {({ focus }) => (
                                    <button
                                        disabled={isRecording}
                                        onClick={() => navigate("/settings")}
                                        className={`${
                                            focus ? "bg-max-900 text-white" : "text-gray-700"
                                        } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                        <SettingsIcon
                                            className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-gray-700"}`}
                                            aria-hidden="true"
                                        />
                                        Settings
                                    </button>
                                )}
                            </MenuItem>
                            {/* Both also have icon shortcuts in the navbar, which drop out on a
                                narrow viewport. They live here at every width, so what is lost
                                there is the shortcut rather than the feature. */}
                            <MenuItem>
                                {({ focus }) => (
                                    <button
                                        disabled={isRecording}
                                        // Settings is a page-level exit and navigating away
                                        // mid-recording is how recordings get stranded, so this
                                        // goes inert with the rest of them - but says so, rather
                                        // than reading as a menu item that does nothing.
                                        title={isRecording ? "Finish the recording to open the Recycle Bin" : undefined}
                                        className={`${
                                            focus ? "bg-max-900 text-white" : "text-gray-700"
                                        } group flex w-full items-center rounded-md px-2 py-2 text-sm ${
                                            isRecording ? "cursor-not-allowed opacity-50" : ""
                                        }`}
                                        onClick={() => navigate("/settings?tab=recycle-bin")}
                                    >
                                        <Trash01Icon
                                            className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-gray-700"}`}
                                            aria-hidden="true"
                                        />
                                        Recycle Bin
                                    </button>
                                )}
                            </MenuItem>
                            <MenuItem>
                                {({ focus }) => (
                                    <button
                                        disabled={isRecording}
                                        // Another page-level exit: /changelog is a sibling
                                        // route with its own PermissionWrapper, so going
                                        // there unmounts AssetLibraryProvider's recorder and
                                        // strands the recording (EN-837 / EN-860). Settings
                                        // and Recycle Bin above hold the same line.
                                        title={isRecording ? "Finish the recording to open Release Notes" : undefined}
                                        className={`${
                                            focus ? "bg-max-900 text-white" : "text-gray-700"
                                        } group flex w-full items-center rounded-md px-2 py-2 text-sm ${
                                            isRecording ? "cursor-not-allowed opacity-50" : ""
                                        }`}
                                        onClick={() => navigate(`/${ENDPOINTS.Changelog}`)}
                                    >
                                        <AnnouncementIcon
                                            className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-gray-700"}`}
                                            aria-hidden="true"
                                        />
                                        Release Notes
                                    </button>
                                )}
                            </MenuItem>

                            {showSwitchClinic && (
                                <MenuItem>
                                    {({ focus }) => (
                                        <button
                                            type="button"
                                            // EN-837: /select-clinic is OUTSIDE PermissionWrapper, so navigating
                                            // there unmounts the in-flight recorder. Disable during the upload-only
                                            // window (not just while recording); and if the dropdown was already
                                            // open when a recording/upload started, defer the nav until idle.
                                            disabled={isRecorderBusy}
                                            onClick={() => {
                                                if (isRecorderActive()) {
                                                    deferredSwitchClinicRunner.current?.schedule(() =>
                                                        navigate(`/${ENDPOINTS.SelectClinic}`),
                                                    );
                                                    return;
                                                }
                                                navigate(`/${ENDPOINTS.SelectClinic}`);
                                            }}
                                            className={`${
                                                focus ? "bg-max-700 text-white" : "text-gray-700"
                                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                        >
                                            <BuildingOffice2Icon
                                                className={`mr-2 h-4 w-4 shrink-0 ${focus ? "text-white" : "text-gray-700"}`}
                                                aria-hidden
                                            />
                                            Switch clinic
                                        </button>
                                    )}
                                </MenuItem>
                            )}
                            <MenuItem>
                                {({ focus }) => (
                                    <button
                                        id={isRecording ? "" : "IntercomCustomLink"}
                                        name="Help Center"
                                        onClick={() => openLink("https://help.gohappydoc.com/en/", true)}
                                        className={`${focus ? "bg-max-900 text-white" : "text-gray-700"} group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                        <HelpCircleIcon
                                            className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-gray-700"}`}
                                            aria-hidden="true"
                                        />
                                        Help Center
                                    </button>
                                )}
                            </MenuItem>
                        </div>

                        <div className="px-1 py-1 ">
                            <MenuItem>
                                {({ focus }) => (
                                    <button
                                        disabled={isRecording}
                                        onClick={() => {
                                            const link =
                                                "https://help.gohappydoc.com/en/articles/8562620-getting-started-with-happydoc";
                                            openLink(link, true);
                                        }}
                                        className={`${
                                            focus ? "bg-max-900 text-white" : "text-gray-700"
                                        } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                        <CursorClickIcon
                                            className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-gray-700"}`}
                                            aria-hidden="true"
                                        />
                                        Getting Started?
                                    </button>
                                )}
                            </MenuItem>
                            {/* <MenuItem>
                                    {({ focus }) => (
                                        <button
                                            disabled={isRecording}
                                            onClick={() => {
                                                let link =
                                                    VITE_PUBLIC_ENV === "production"
                                                        ? "https://app.gohappydoc.com/?product_tour_id=570626"
                                                        : VITE_PUBLIC_ENV === "development"
                                                            ? "https://happy-doc-3-0-git-develop-happydoc-s-team.vercel.app/?product_tour_id=570626"
                                                            : "";
                                                if (!link) return;
                                                openLink(link);
                                            }}
                                            className={`${focus ? 'bg-max-900 text-white' : 'text-gray-700'
                                                } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                        >
                                            <RouteIcon className={`mr-2 h-4 w-4 ${focus ? 'text-white' : 'text-gray-700'}`} aria-hidden="true" />
                                            Product Tour
                                        </button>
                                    )}
                                </MenuItem> */}
                        </div>

                        {/* Admin Section */}
                        {isAdmin && (
                            <div className="px-1 py-1 border-t border-gray-200">
                                <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Admin
                                </div>
                                <MenuItem>
                                    {({ focus }) => (
                                        <button
                                            disabled={isRecording}
                                            onClick={() => navigate("/admin/impersonation")}
                                            className={`${
                                                focus ? "bg-blue-600 text-white" : "text-blue-600"
                                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                        >
                                            <UserIcon
                                                className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-blue-600"}`}
                                                aria-hidden="true"
                                            />
                                            User Impersonation
                                        </button>
                                    )}
                                </MenuItem>
                                <MenuItem>
                                    {({ focus }) => (
                                        <button
                                            disabled={isRecording}
                                            onClick={() => navigate("/admin/checkout")}
                                            className={`${
                                                focus ? "bg-blue-600 text-white" : "text-blue-600"
                                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                        >
                                            <CreditCardIcon
                                                className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-blue-600"}`}
                                                aria-hidden="true"
                                            />
                                            Sales Checkout
                                        </button>
                                    )}
                                </MenuItem>
                                {import.meta.env.DEV && (
                                    <MenuItem>
                                        {({ focus }) => {
                                            const isEnabled = localStorage.getItem("tourDevMode") === "true";
                                            return (
                                                <button
                                                    disabled={isRecording}
                                                    onClick={() => {
                                                        localStorage.setItem(
                                                            "tourDevMode",
                                                            isEnabled ? "false" : "true",
                                                        );
                                                        window.location.reload();
                                                    }}
                                                    className={`${
                                                        focus ? "bg-blue-600 text-white" : "text-blue-600"
                                                    } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                                >
                                                    <RouteIcon
                                                        className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-blue-600"}`}
                                                        aria-hidden="true"
                                                    />
                                                    Tour Dev Tools {isEnabled ? "(On)" : "(Off)"}
                                                </button>
                                            );
                                        }}
                                    </MenuItem>
                                )}
                            </div>
                        )}

                        {/* Impersonation Section */}
                        {isImpersonating && impersonationSession && (
                            <div className="px-1 py-1 border-t border-yellow-200 bg-yellow-50">
                                <div className="px-2 py-1 text-xs font-medium text-yellow-700 uppercase tracking-wider">
                                    Impersonating
                                </div>
                                <div className="px-2 py-2 text-sm text-yellow-800">
                                    <div className="font-medium">{impersonationSession.targetEmail}</div>
                                    <div className="text-xs text-yellow-600">
                                        Admin: {impersonationSession.adminEmail}
                                    </div>
                                </div>
                                <MenuItem>
                                    {({ focus }) => (
                                        <button
                                            // EN-837: teardown-sensitive — stopImpersonation schedules a deferred
                                            // teardown (clear session → clearAllCachedData → nav). Gate on the SAME
                                            // isRecorderBusy predicate as Sign Out / Switch clinic so a click during
                                            // the upload-only / completing / finalizing window can't silently defer
                                            // with no feedback — the greyed, disabled control IS the feedback.
                                            disabled={isRecorderBusy}
                                            onClick={async () => {
                                                const { ImpersonationService, RECORDING_IN_OTHER_TAB_ERROR } =
                                                    await import("@/common/lib/impersonationService");
                                                const result = await ImpersonationService.stopImpersonation();
                                                if (!result.success) {
                                                    console.error("Failed to stop impersonation:", result.error);
                                                    // EN-1313: a stop refused because another tab is recording
                                                    // is expected and actionable, so say why.
                                                    if (result.error === RECORDING_IN_OTHER_TAB_ERROR) {
                                                        toast.error(result.error);
                                                    }
                                                }
                                            }}
                                            className={`${
                                                focus ? "bg-yellow-600 text-white" : "text-yellow-700"
                                            } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                        >
                                            <LogOutIcon
                                                className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-yellow-700"}`}
                                                aria-hidden="true"
                                            />
                                            Stop Impersonation
                                        </button>
                                    )}
                                </MenuItem>
                            </div>
                        )}

                        <div className="px-1 py-1">
                            <MenuItem>
                                {({ focus }) => (
                                    <button
                                        disabled={isRecorderBusy}
                                        onClick={signOut}
                                        className={`${
                                            focus ? "bg-max-900 text-white" : "text-gray-700"
                                        } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                                    >
                                        <LogOutIcon
                                            className={`mr-2 h-4 w-4 ${focus ? "text-white" : "text-gray-700"}`}
                                            aria-hidden="true"
                                        />
                                        Sign Out
                                    </button>
                                )}
                            </MenuItem>
                        </div>
                    </MenuItems>
                </Transition>
            </Menu>
            <LocalStorageModal disabled={isRecording} setOpen={setOpenModal} open={openModal} showButton={false} />
            {isTrial && (
                <PlanUpgradeModal
                    isOpen={showUpgradeModal}
                    onClose={() => setShowUpgradeModal(false)}
                    currentTier="trial"
                    billingType="trial"
                />
            )}
        </Fragment>
    );
};

export default ProfileMenu;
